import { useResizeObserver } from '@vueuse/core';
import { Ref, nextTick, ref, watch } from 'vue';

export function useChipOverflow(items: Ref<unknown[]>) {
  const selectedItemsContainer = ref<HTMLElement | null>(null);
  const overflowTag = ref<HTMLElement | null>(null);
  const tagRefs = ref<HTMLElement[]>([]);
  const visibleCount = ref(Number.MAX_SAFE_INTEGER);

  function setTagRef(el: unknown, index: number) {
    if (el && typeof el === 'object' && '$el' in el) {
      tagRefs.value[index] = el.$el as HTMLElement;
    }
  }

  async function calculateVisibleCount() {
    if (!selectedItemsContainer.value || !tagRefs.value.length) {
      visibleCount.value = Number.MAX_SAFE_INTEGER;
      return;
    }

    visibleCount.value = Number.MAX_SAFE_INTEGER;
    await nextTick();

    if (!selectedItemsContainer.value) return;

    const containerWidth =
      selectedItemsContainer.value.getBoundingClientRect().width;
    const gap =
      parseFloat(getComputedStyle(selectedItemsContainer.value).gap) || 0;
    const overflowWidth =
      overflowTag.value?.getBoundingClientRect().width ?? 50;

    let currentWidth = 0;
    let overflowIndex = -1;

    for (let i = 0; i < tagRefs.value.length; i++) {
      const tagWidth = tagRefs.value[i]?.getBoundingClientRect().width ?? 0;
      const widthToAdd = tagWidth + (i > 0 ? gap : 0);

      if (currentWidth + widthToAdd > containerWidth) {
        overflowIndex = i;
        break;
      }

      currentWidth += widthToAdd;
    }

    if (overflowIndex === -1) return;

    for (let fit = overflowIndex; fit >= 0; fit--) {
      let width = 0;

      for (let j = 0; j < fit; j++) {
        width +=
          (tagRefs.value[j]?.getBoundingClientRect().width ?? 0) +
          (j > 0 ? gap : 0);
      }

      if (width + gap + overflowWidth <= containerWidth) {
        visibleCount.value = fit;
        return;
      }
    }

    visibleCount.value = 0;
  }

  watch(items, async () => {
    tagRefs.value = [];
    await nextTick();
    calculateVisibleCount();
  });

  useResizeObserver(selectedItemsContainer, () => {
    requestAnimationFrame(calculateVisibleCount);
  });

  return {
    selectedItemsContainer,
    overflowTag,
    visibleCount,
    setTagRef,
  };
}
